import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { CoursPreview } from "../components/CoursPreview";
import { WordDiff } from "../components/WordDiff";
import { useAuth } from "../context/AuthContext";
import LitteratureAtelier from "./LitteratureAtelier";
import ProjetsAppels from "./ProjetsAppels";
import CurriculumMotivation from "./CurriculumMotivation";
import ReunionAtelier from "./ReunionAtelier";
import RomanAtelier from "./RomanAtelier";
import { BookOpen, Feather, ScrollText, Sparkles, Loader2, Download, Drama, Landmark, Library, RefreshCw, Trash2, FolderOpen, X, Upload, FileText, Image as ImageIcon, Pencil, Check, GraduationCap, BookMarked, BookText, ChevronDown, ChevronUp, HelpCircle, ListChecks, ClipboardCheck, Quote, Plus, Wand2, Save, SlidersHorizontal, RotateCcw, GitCompare } from "lucide-react";

const GENRES = [
  { key: "cours", label: "Cours académique", icon: GraduationCap, desc: "Leçons, séquences, objectifs, exercices & évaluation" },
  { key: "livre", label: "Livre", icon: BookOpen, desc: "Chapitres, résumé, personnages, plan narratif" },
  { key: "poeme", label: "Poème", icon: Feather, desc: "Sonnet, haïku, vers libre, ode" },
  { key: "essai", label: "Essai", icon: ScrollText, desc: "Introduction, parties, conclusion argumentée" },
  { key: "epopee", label: "Épopée", icon: Landmark, desc: "Chants, héros, registre épique" },
  { key: "theatre", label: "Pièce de théâtre", icon: Drama, desc: "Actes, scènes, didascalies, dialogues" },
  { key: "roman", label: "Roman", icon: BookMarked, desc: "Intrigue, personnages, chapitres, arcs narratifs", roles: ["professionnel", "gestionnaire", "admin", "medecin_dim", "tim", "gestionnaire_donnees"] },
];
const POEM_FORMS = [
  { v: "sonnet", l: "Sonnet" }, { v: "haiku", l: "Haïku" },
  { v: "vers_libre", l: "Vers libre" }, { v: "ode", l: "Ode" },
];
const TITLES = { cours: "Cours académique", livre: "Livre", poeme: "Poème", essai: "Essai", epopee: "Épopée", theatre: "Pièce de théâtre", roman: "Roman" };
const TONES = [
  { v: "classique", l: "Classique" }, { v: "moderne", l: "Moderne" }, { v: "jeunesse", l: "Jeunesse" },
];
const CITATION_STYLES = [
  { v: "vancouver", l: "Vancouver" }, { v: "apa", l: "APA" },
  { v: "chicago", l: "Chicago" }, { v: "mla", l: "MLA" },
];
const EVAL_LEVELS = [
  { v: "primaire", l: "Primaire", x: "indulgente" },
  { v: "college", l: "Collège", x: "indulgente" },
  { v: "lycee", l: "Lycée", x: "standard" },
  { v: "superieur", l: "Supérieur / Universitaire", x: "exigeante" },
  { v: "pro", l: "Professionnel / Expert", x: "très exigeante" },
];
const MANUAL_CONTENT = `Ce manuel décrit, étape par étape, la procédure de création d'un cours académique à l'aide de l'Atelier d'écriture créative de Vitalis Digital Store.

## 1. Prérequis et accès

Prérequis :
• Être connecté à votre compte (rôle Client, Professionnel ou Administrateur).
• Disposer d'une connexion Internet stable (la génération fait appel à l'IA).

Accès à l'Atelier :
• Connectez-vous à la plateforme depuis la page d'accueil.
• Dans la barre de navigation principale, cliquez sur « Atelier d'écriture ».
• Sur la page de l'Atelier, sélectionnez la vignette « Cours académique » (sélectionnée par défaut, en haut de la liste des genres).

Le formulaire de paramétrage du cours s'affiche alors sur la droite.

## 2. Saisie des consignes et paramètres

Renseignez les champs dans l'ordre suivant :
• Sujet / thème de l'œuvre (obligatoire, minimum 3 caractères) : ex. « Introduction à la biologie cellulaire ».
• Style d'auteur (ton / registre) : choisissez le registre souhaité (ex. Classique).
• Longueur du texte : Court, Moyen ou Long.

Paramètres spécifiques au cours académique :
• Public / niveau : précisez le public cible et le niveau attendu (ex. « L1 médecine », « Master »).
• Nombre de leçons / chapitres : définissez le nombre de séquences (1 à 30).
• Durée estimée (volume horaire) : saisie libre (ex. « 20h »). Ce volume calibre la densité et la profondeur de chaque leçon.
• Enseignant / auteur (optionnel) : ce nom figurera sur la page de garde du livret imprimé.

## 3. Gestion des pièces jointes multiples

Vous pouvez fournir plusieurs documents de référence pour guider l'IA.

Ajouter des documents :
• Formats acceptés : PDF, Word (.docx) et texte (.txt).
• Glissez-déposez vos fichiers dans la zone prévue, ou cliquez sur cette zone pour les sélectionner. Plusieurs fichiers sont acceptés simultanément.

Vérifier et gérer la liste :
• Chaque fichier importé apparaît dans une liste sous la zone de dépôt, avec son nom et sa taille (en Ko).
• Pour retirer un fichier avant la validation, cliquez sur l'icône ✕ à droite de la ligne concernée. La suppression est individuelle : les autres fichiers restent intacts.

Note de vérification : avant de lancer la génération, contrôlez que tous vos documents apparaissent bien dans la liste et que leur taille n'est pas nulle. Un message « Pièce(s) jointe(s) importée(s) » confirme chaque import réussi. Seuls les documents présents dans la liste seront réellement pris en compte par l'IA ; en cas d'erreur, réimportez le fichier concerné.

## 4. Lancement et génération

• Vérifiez une dernière fois le sujet, les paramètres et la liste des pièces jointes.
• Cliquez sur « Générer avec l'IA ».
• Un indicateur de progression s'affiche pendant le traitement. La rédaction d'un cours complet peut prendre jusqu'à 2 à 3 minutes selon le volume horaire et le nombre de leçons : ne fermez pas la page pendant ce temps.
• À la fin, le cours généré s'affiche et est enregistré automatiquement dans votre bibliothèque (message « Œuvre enregistrée dans la bibliothèque »).

## 5. Exploitation des résultats

Une fois le cours généré, plusieurs actions sont disponibles :
• Consulter le contenu directement à l'écran (titre, introduction, objectifs, plan, leçons, synthèse, glossaire, évaluation).
• Aperçu paginé : ouvrez la vue plein écran pour naviguer page par page, visualiser le nombre de mots par leçon (avec un badge « Leçon courte — à étoffer » le cas échéant), régénérer une leçon isolée, et imprimer un livret précédé d'une page de garde (logo, titre, niveau, enseignant, date).
• Illustration de cours : générez une couverture pédagogique par IA, insérée en première page des exports.
• Téléverser / exporter : téléchargez le cours au format PDF ou Word pour le diffuser, l'archiver ou l'intégrer à vos supports.
• Bibliothèque : retrouvez, renommez ou rouvrez vos cours enregistrés depuis « Ma bibliothèque ». À la réouverture, tous les paramètres (niveau, leçons, durée, enseignant) sont restaurés.

## Bon à savoir

• Le contenu généré est un support de base : relisez-le et adaptez-le à votre contexte avant diffusion.
• Vous pouvez régénérer une leçon autant de fois que nécessaire sans repartir de zéro.`;


const HELP_TOPICS = {
  subject: { title: "Sujet / thème du cours", body: ["Indiquez le sujet précis du cours (ex. « Introduction à la biologie cellulaire »). Ce champ est obligatoire et doit contenir au moins 3 caractères.", "Plus le sujet est clair et délimité, plus le plan et les leçons générés seront pertinents."] },
  tone: { title: "Style d'auteur (ton / registre)", body: ["Choisissez le registre d'écriture souhaité (Classique, Moderne ou Jeunesse).", "Pour un cours académique, le registre Classique — soutenu et intemporel — est généralement le plus adapté."] },
  length: { title: "Longueur du texte", body: ["Réglez le volume global du contenu généré : Court, Moyen ou Long.", "Pour un cours, ce réglage agit avec la « Durée estimée » : combinez-les pour ajuster la densité finale."] },
  attach: { title: "Pièces jointes d'inspiration", body: ["Joignez un ou plusieurs documents de référence (PDF, Word, TXT) que l'IA analysera pour nourrir le contenu du cours.", "Glissez-déposez vos fichiers ou cliquez sur la zone. Chaque fichier apparaît avec son nom et sa taille ; l'icône ✕ permet d'en retirer un individuellement.", "Vérifiez que tous vos documents figurent bien dans la liste avant de lancer la génération : seuls les fichiers listés sont pris en compte."] },
  level: { title: "Public / niveau", body: ["Précisez le public cible et le niveau académique attendu (ex. « L1 médecine », « Master », « Lycée »).", "Cette information calibre le vocabulaire, la profondeur et les prérequis supposés des leçons."] },
  lessons: { title: "Nombre de leçons / chapitres", body: ["Définissez le nombre de séquences pédagogiques à générer (de 1 à 30).", "Chaque leçon comprend ses objectifs, son contenu, des exercices et une évaluation."] },
  hours: { title: "Durée estimée (volume horaire)", body: ["Saisie libre du volume horaire visé (ex. « 20h »).", "Ce volume calibre la densité et la profondeur de chaque leçon (comptez environ 1 à 2 heures de cours par leçon)."] },
  teacher: { title: "Enseignant / auteur", body: ["Champ optionnel. Le nom saisi apparaîtra sur la page de garde du livret imprimé.", "Laissez vide si vous ne souhaitez pas afficher d'auteur."] },
};


export default function AtelierEcriture() {
  const [section, setSection] = useState("creative");
  const { user } = useAuth();
  const canProjets = ["professionnel", "gestionnaire", "admin", "medecin_dim", "tim", "gestionnaire_donnees"].includes(user?.role);
  const canGenre = (g) => !g.roles || g.roles.includes(user?.role);
  const visibleGenres = GENRES.filter(canGenre);
  useEffect(() => { const g = GENRES.find((x) => x.key === genre); if (g && !canGenre(g)) setGenre("cours"); }, [user]); // eslint-disable-line
  const [genre, setGenre] = useState("cours");
  const [f, setF] = useState({ subject: "", tone: "classique", length: "moyen", form: "vers_libre", chapters: 8, literary_genre: "", thesis: "", hero: "", setting: "", acts: 3, lessons: 6, level: "", target_hours: "", teacher: "" });
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState("");
  const [exporting, setExporting] = useState("");
  const [continuing, setContinuing] = useState(false);
  const [workId, setWorkId] = useState(null);
  const [library, setLibrary] = useState([]);
  const [showLib, setShowLib] = useState(false);
  const [regenIdx, setRegenIdx] = useState(null);
  const [inspFiles, setInspFiles] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [cover, setCover] = useState(null);
  const [coverLoading, setCoverLoading] = useState(false);
  const [coverOverlay, setCoverOverlay] = useState(true);
  const [coverPos, setCoverPos] = useState("bas");
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [libQuery, setLibQuery] = useState("");
  const [libGenre, setLibGenre] = useState("all");
  const [libSort, setLibSort] = useState("date");
  const [showPreview, setShowPreview] = useState(false);
  const [exportingManual, setExportingManual] = useState("");
  const [helpKey, setHelpKey] = useState(null);
  const [workStatus, setWorkStatus] = useState("brouillon");
  const [busy, setBusy] = useState("");          // action IA en cours
  const [changes, setChanges] = useState(null);   // {kind, items}
  const [plan, setPlan] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [references, setReferences] = useState([]);
  const [citationStyle, setCitationStyle] = useState("vancouver");
  const [newRef, setNewRef] = useState("");
  const [libStatus, setLibStatus] = useState("all");
  const [rubricsAll, setRubricsAll] = useState({});
  const [rubric, setRubric] = useState([]);
  const [showRubric, setShowRubric] = useState(false);
  const [savingRubric, setSavingRubric] = useState(false);
  const [evalLevel, setEvalLevel] = useState("lycee");
  const [showDiff, setShowDiff] = useState(true);
  const [doiInput, setDoiInput] = useState("");
  const [importingRef, setImportingRef] = useState("");
  const bibFileRef = useRef(null);

  const stats = useMemo(() => {
    const plain = (result || "").replace(/[#>*_`|[\]()-]/g, " ");
    const words = (plain.match(/[\p{L}\p{N}'’-]+/gu) || []).length;
    const sentences = (plain.match(/[.!?…]+/g) || []).length;
    const mins = Math.max(1, Math.round(words / 200));
    return { words, sentences, mins };
  }, [result]);
  const assistPollRef = useRef(null);
  const outputRef = useRef(null);

  useEffect(() => () => { assistPollRef.current && clearInterval(assistPollRef.current); }, []);
  useEffect(() => { api.get("/creative/rubrics").then(({ data }) => setRubricsAll(data.rubrics || {})).catch(() => {}); }, []);
  useEffect(() => { const r = rubricsAll[genre]; if (r) setRubric((r.criteria || []).map((c) => ({ ...c }))); }, [genre, rubricsAll]);
  const rubricSum = rubric.reduce((s, c) => s + (Number(c.max) || 0), 0);

  const HelpBtn = ({ k }) => genre === "cours" ? (
    <button type="button" onClick={() => setHelpKey(k)} data-testid={`atelier-help-${k}`} title="Aide sur ce champ"
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-[#0E7C7B]/10 hover:text-[#0E7C7B]">
      <HelpCircle size={15} />
    </button>
  ) : null;

  const set = (patch) => setF((p) => ({ ...p, ...patch }));

  const processFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setAttaching(true);
    try {
      for (const file of files) {
        try {
          const fd = new FormData(); fd.append("file", file);
          const { data } = await api.post("/recherche/ai/extract-text", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setInspFiles((prev) => [...prev, { name: data.filename || file.name, size: file.size, chars: (data.text || "").length, text: data.text || "" }]);
        } catch (err) { toast.error(apiError(err, `Extraction impossible : ${file.name}`)); }
      }
      toast.success("Pièce(s) jointe(s) importée(s)");
    } finally { setAttaching(false); }
  };
  const onAttachChange = (e) => { processFiles(e.target.files); e.target.value = ""; };
  const onDropFiles = (e) => { e.preventDefault(); setDragOver(false); processFiles(e.dataTransfer.files); };
  const removeInspFile = (i) => setInspFiles((prev) => prev.filter((_, k) => k !== i));

  const generateCover = async () => {
    setCoverLoading(true);
    try {
      const { data } = await api.post("/creative/cover", { genre, title: (result.match(/^#\s+(.+)$/m) || [])[1] || f.subject, subject: f.subject });
      setCover(data.image_b64);
      toast.success("Couverture générée");
    } catch (e) { toast.error(apiError(e, "Génération de couverture indisponible")); } finally { setCoverLoading(false); }
  };

  const renameWork = async (id) => {
    const t = (renameVal || "").trim();
    if (!t) { setRenameId(null); return; }
    try { await api.put(`/creative/works/${id}/rename`, { title: t }); setRenameId(null); loadLibrary(); toast.success("Titre modifié"); }
    catch (e) { toast.error(apiError(e, "Renommage impossible")); }
  };

  const loadLibrary = async () => {
    try { const { data } = await api.get("/creative/works"); setLibrary(data.works || []); }
    catch { /* silencieux */ }
  };
  useEffect(() => { loadLibrary(); }, []);

  const pollJob = (jid) => new Promise((resolve) => {
    const poll = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/creative/job/${jid}`);
        if (j.status === "done") { clearInterval(poll); resolve(j.result); }
        else if (j.status === "error") { clearInterval(poll); toast.error(j.error || "Erreur IA"); resolve(null); }
      } catch { clearInterval(poll); resolve(null); }
    }, 3000);
  });

  const saveWork = async (content = result, id = workId, extra = {}) => {
    try {
      const attachments = inspFiles.map((x) => ({ name: x.name, size: x.size, chars: x.chars, text: x.text }));
      const { data } = await api.post("/creative/works", {
        id, genre, subject: f.subject, tone: f.tone,
        status: extra.status !== undefined ? extra.status : workStatus,
        meta: {
          form: f.form, chapters: f.chapters, literary_genre: f.literary_genre, thesis: f.thesis, hero: f.hero, setting: f.setting, acts: f.acts, lessons: f.lessons, level: f.level, target_hours: f.target_hours, teacher: f.teacher,
          attachments,
          evaluation: extra.evaluation !== undefined ? extra.evaluation : evaluation,
          references: extra.references !== undefined ? extra.references : references,
          citationStyle, eval_level: evalLevel,
        },
        content,
      });
      setWorkId(data.id);
      loadLibrary();
      return data.id;
    } catch { /* ne bloque pas l'UX */ return id; }
  };

  const run = async () => {
    if ((f.subject || "").trim().length < 3) { toast.error("Indiquez un sujet ou un thème."); return; }
    setStatus("running"); setResult(""); setWorkId(null); setCover(null);
    setWorkStatus("brouillon"); setEvaluation(null); setReferences([]); setChanges(null); setPlan(null);
    try {
      const inspiration = inspFiles.map((x) => x.text).filter(Boolean).join("\n\n----\n\n");
      const { data } = await api.post("/creative/generate", { genre, ...f, chapters: Number(f.chapters) || 8, acts: Number(f.acts) || 3, lessons: Number(f.lessons) || 6, target_hours: f.target_hours || "", inspiration });
      const text = await pollJob(data.job_id);
      if (text != null) { setResult(text); setStatus("done"); await saveWork(text, null); toast.success("Œuvre enregistrée dans la bibliothèque"); }
      else setStatus("error");
    } catch (e) { toast.error(apiError(e)); setStatus("idle"); }
  };

  const continueWriting = async () => {
    setContinuing(true);
    try {
      const { data } = await api.post("/creative/continue", { genre, existing_text: result, tone: f.tone });
      const text = await pollJob(data.job_id);
      if (text != null) { const merged = result + "\n\n" + text; setResult(merged); await saveWork(merged); }
    } catch (e) { toast.error(apiError(e)); } finally { setContinuing(false); }
  };

  const parseSections = (content) => {
    const lines = (content || "").split("\n");
    const heads = [];
    for (let i = 0; i < lines.length; i++) if (/^##\s+/.test(lines[i])) heads.push(i);
    const secs = [];
    for (let k = 0; k < heads.length; k++) {
      const startLine = heads[k];
      const bound = k + 1 < heads.length ? heads[k + 1] : lines.length;
      let endLine = bound;
      for (let j = startLine + 1; j < bound; j++) if (/^#\s+/.test(lines[j])) { endLine = j; break; }
      secs.push({ title: lines[startLine].replace(/^##\s+/, "").trim(), raw: lines.slice(startLine, endLine).join("\n"), startLine, endLine });
    }
    return secs;
  };

  const regenerateSection = async (sec, idx) => {
    setRegenIdx(idx);
    try {
      const context = result.replace(sec.raw, "").slice(0, 6000);
      const { data } = await api.post("/creative/regenerate-section", { genre, tone: f.tone, section_title: sec.title, section_text: sec.raw, context });
      const text = await pollJob(data.job_id);
      if (text != null) {
        const lines = result.split("\n");
        const before = lines.slice(0, sec.startLine).join("\n");
        const after = lines.slice(sec.endLine).join("\n");
        const merged = [before, text.trim(), after].filter((s) => s !== "").join("\n\n");
        setResult(merged); await saveWork(merged);
        toast.success("Section régénérée");
      }
    } catch (e) { toast.error(apiError(e)); } finally { setRegenIdx(null); }
  };

  const regenerateByIndex = (secIdx) => {
    const secs = parseSections(result);
    if (secs[secIdx]) regenerateSection(secs[secIdx], secIdx);
  };

  const openWork = async (id) => {
    try {
      const { data } = await api.get(`/creative/works/${id}`);
      setGenre(data.genre);
      const m = data.meta || {};
      setF({ subject: data.subject || "", tone: data.tone || "classique", length: m.length || "moyen", form: m.form || "vers_libre", chapters: m.chapters || 8, literary_genre: m.literary_genre || "", thesis: m.thesis || "", hero: m.hero || "", setting: m.setting || "", acts: m.acts || 3, lessons: m.lessons || 6, level: m.level || "", target_hours: m.target_hours || "", teacher: m.teacher || "" });
      setResult(data.content || ""); setWorkId(data.id); setStatus("done"); setShowLib(false); setCover(null);
      setWorkStatus(data.status || "brouillon");
      setEvaluation(m.evaluation || null); setReferences(m.references || []); setCitationStyle(m.citationStyle || "vancouver");
      setEvalLevel(m.eval_level || "lycee");
      setChanges(null); setPlan(null);
      setInspFiles((m.attachments || []).map((a) => ({ name: a.name, size: a.size || 0, chars: a.chars || 0, text: a.text || "" })));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiError(e, "Ouverture impossible")); }
  };

  const deleteWork = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/creative/works/${id}`); if (id === workId) setWorkId(null); loadLibrary(); }
    catch { toast.error("Suppression impossible"); }
  };

  const runAssist = (action) => {
    const content = result || "";
    if (["correct", "improve", "evaluate"].includes(action) && content.trim().length < 10) { toast.error("Générez ou saisissez d'abord un texte."); return; }
    const attachments_text = inspFiles.map((x) => x.text).filter(Boolean).join("\n\n----\n\n");
    setBusy(action);
    if (assistPollRef.current) clearInterval(assistPollRef.current);
    if (action === "correct" || action === "improve") setChanges(null);
    if (action === "plan") setPlan(null);
    if (action === "evaluate") setEvaluation(null);
    api.post("/creative/ai", { action, genre, content, attachments_text, level: f.level, tone: f.tone, citation_style: citationStyle, eval_level: evalLevel, rubric: action === "evaluate" ? rubric.map((c) => ({ critere: c.critere, max: Number(c.max) || 0 })) : [] })
      .then(({ data }) => {
        assistPollRef.current = setInterval(async () => {
          try {
            const { data: j } = await api.get(`/creative/ai/job/${data.job_id}`);
            if (j.status === "done") {
              clearInterval(assistPollRef.current); setBusy("");
              const r = j.result || {};
              if (action === "correct") { const nc = r.corrected || content; setResult(nc); setChanges({ kind: "correct", items: r.changes || [], original: content, revised: nc }); setShowDiff(true); saveWork(nc); toast.success("Texte corrigé"); }
              else if (action === "improve") { const nc = r.improved || content; setResult(nc); setChanges({ kind: "improve", items: r.suggestions || [], original: content, revised: nc }); setShowDiff(true); saveWork(nc); toast.success("Texte amélioré"); }
              else if (action === "plan") { setPlan(r); toast.success("Plan proposé"); }
              else if (action === "evaluate") { setEvaluation(r); saveWork(result, workId, { evaluation: r }); toast.success(`Note : ${r.note_globale ?? "?"}/20`); }
              else if (action === "refs") { setReferences((p) => { const merged = [...p, ...(r.references || [])]; saveWork(result, workId, { references: merged }); return merged; }); toast.success("Références suggérées"); }
            } else if (j.status === "error") { clearInterval(assistPollRef.current); setBusy(""); toast.error(j.error || "Service IA indisponible"); }
          } catch { clearInterval(assistPollRef.current); setBusy(""); }
        }, 2500);
      })
      .catch((err) => { setBusy(""); toast.error(apiError(err, "Service IA indisponible")); });
  };

  const insertCitation = (ref) => {
    const marker = citationStyle === "apa" ? ` (${(ref.auteurs || "Auteur").split(" ")[0]}, ${ref.annee || "s.d."})` : ` [${references.indexOf(ref) + 1}]`;
    const ta = outputRef.current;
    if (!ta) { const nc = result + marker; setResult(nc); saveWork(nc); return; }
    const s = ta.selectionStart ?? result.length, e = ta.selectionEnd ?? result.length;
    const nc = result.slice(0, s) + marker + result.slice(e);
    setResult(nc);
    requestAnimationFrame(() => { ta.focus(); const p = s + marker.length; try { ta.setSelectionRange(p, p); } catch { /* */ } });
    saveWork(nc);
  };
  const addManualRef = () => { if (newRef.trim()) { setReferences((p) => { const merged = [...p, { formatee: newRef.trim() }]; saveWork(result, workId, { references: merged }); return merged; }); setNewRef(""); } };
  const importDoi = async () => {
    if (!doiInput.trim()) return;
    setImportingRef("doi");
    try {
      const { data } = await api.post("/creative/refs/doi", { doi: doiInput.trim(), style: citationStyle });
      setReferences((p) => { const merged = [...p, data.reference]; saveWork(result, workId, { references: merged }); return merged; });
      setDoiInput(""); toast.success("Référence importée depuis le DOI");
    } catch (e) { toast.error(apiError(e, "DOI introuvable")); }
    finally { setImportingRef(""); }
  };
  const importBib = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImportingRef("bib");
    try {
      const content = await file.text();
      const { data } = await api.post("/creative/refs/bib", { content });
      setReferences((p) => { const merged = [...p, ...(data.references || [])]; saveWork(result, workId, { references: merged }); return merged; });
      toast.success(`${(data.references || []).length} référence(s) importée(s)`);
    } catch (err) { toast.error(apiError(err, "Fichier .bib invalide")); }
    finally { setImportingRef(""); if (bibFileRef.current) bibFileRef.current.value = ""; }
  };
  // Recalcule les marqueurs numériques [n] du texte selon un nouvel ordre (newOrder = anciens index dans le nouvel ordre).
  const renumberText = (text, newOrder) => {
    const map = {};
    newOrder.forEach((oldIdx, p) => { map[oldIdx + 1] = p + 1; });
    return (text || "").replace(/\s?\[(\d+)\]/g, (m, num) => {
      const n = Number(num);
      return map[n] ? ` [${map[n]}]` : "";
    }).replace(/[ \t]{2,}/g, " ");
  };
  const applyRefOrder = (newOrder) => {
    const newRefs = newOrder.map((oldIdx) => references[oldIdx]);
    const nc = renumberText(result, newOrder);
    setReferences(newRefs); setResult(nc); saveWork(nc, workId, { references: newRefs });
  };
  const moveRef = (from, to) => {
    if (to < 0 || to >= references.length) return;
    const newOrder = references.map((_, k) => k);
    const [m] = newOrder.splice(from, 1);
    newOrder.splice(to, 0, m);
    applyRefOrder(newOrder);
  };
  const removeRef = (i) => applyRefOrder(references.map((_, k) => k).filter((k) => k !== i));
  const insertPlan = () => { if (plan?.plan_markdown) { const nc = result + "\n\n" + plan.plan_markdown; setResult(nc); saveWork(nc); toast.success("Plan inséré dans le texte"); } };

  const setCrit = (i, field, val) => setRubric((p) => p.map((c, k) => (k === i ? { ...c, [field]: val } : c)));
  const addCrit = () => setRubric((p) => [...p, { critere: "", max: "" }]);
  const removeCrit = (i) => setRubric((p) => p.filter((_, k) => k !== i));
  const resetRubric = async () => {
    try {
      const { data } = await api.delete(`/creative/rubrics/${genre}`);
      setRubric(data.criteria.map((c) => ({ ...c })));
      setRubricsAll((p) => ({ ...p, [genre]: { criteria: data.criteria, custom: false } }));
      toast.success("Barème réinitialisé au modèle par défaut");
    } catch (e) { toast.error(apiError(e, "Échec de la réinitialisation")); }
  };
  const saveRubric = async () => {
    if (rubric.some((c) => !String(c.critere).trim())) { toast.error("Chaque critère doit avoir un nom."); return; }
    if (rubricSum !== 20) { toast.error(`Le total des points doit faire 20 (actuel : ${rubricSum}).`); return; }
    setSavingRubric(true);
    try {
      const { data } = await api.put(`/creative/rubrics/${genre}`, { criteria: rubric.map((c) => ({ critere: c.critere, max: Number(c.max) || 0 })) });
      setRubricsAll((p) => ({ ...p, [genre]: { criteria: data.criteria, custom: true } }));
      setRubric(data.criteria.map((c) => ({ ...c })));
      toast.success("Barème enregistré — réutilisé pour ce genre");
    } catch (e) { toast.error(apiError(e, "Échec de l'enregistrement")); }
    finally { setSavingRubric(false); }
  };

  const buildExtras = () => {
    let extra = "";
    if (evaluation) {
      const lvl = EVAL_LEVELS.find((l) => l.v === evalLevel)?.l || "Standard";
      extra += `\n\n## Grille de notation — Note : ${evaluation.note_globale ?? "?"} / ${evaluation.sur || 20}\n`;
      extra += `Niveau d'exigence : ${lvl}\n\n`;
      extra += `| Critère | Note | Appréciation |\n| --- | --- | --- |\n`;
      extra += (evaluation.criteres || []).map((c) => `| ${String(c.critere || "").replace(/\|/g, "/")} | ${c.note}/${c.max} | ${String(c.commentaire || "").replace(/\|/g, "/")} |`).join("\n");
      if (evaluation.appreciation_generale) extra += `\n\n**Appréciation générale :** ${evaluation.appreciation_generale}`;
    }
    if (references.length) {
      extra += `\n\n## Références bibliographiques (${CITATION_STYLES.find((s) => s.v === citationStyle)?.l || ""})\n`;
      extra += references.map((r, i) => `- [${i + 1}] ${r.formatee || `${r.auteurs || ""} (${r.annee || ""}). ${r.titre || ""}. ${r.source || ""}`}`).join("\n");
    }
    return extra;
  };

  const exportTxt = () => {
    const title = f.subject ? `${TITLES[genre]} — ${f.subject}` : TITLES[genre];
    const body = `Cabinet CARIS Santé — Vitalis Digital\n${title}\n${"=".repeat(Math.min(60, title.length + 24))}\n\n` + (result || "") + buildExtras();
    const blob = new Blob([body], { type: "text/plain;charset=utf-8" });
    const u = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = u; a.download = "atelier-ecriture.txt"; a.click(); URL.revokeObjectURL(u);
  };

  const exportDoc = async (format) => {
    setExporting(format);
    try {
      const title = f.subject ? `${TITLES[genre]} — ${f.subject}` : TITLES[genre];
      const fullContent = (result || "") + buildExtras();
      const res = await api.post("/recherche/ai/export", { title, content: fullContent, format, draft: false, cover_image_b64: cover || "", overlay_title: !!cover && coverOverlay, cover_title_position: coverPos }, { responseType: "blob" });
      const u = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = u; a.download = format === "docx" ? "atelier-ecriture.docx" : "atelier-ecriture.pdf"; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiError(e, "Export indisponible")); } finally { setExporting(""); }
  };

  const exportManual = async (format) => {
    setExportingManual(format);
    try {
      const res = await api.post("/recherche/ai/export", { title: "Manuel de procédures", content: MANUAL_CONTENT, format, draft: false }, { responseType: "blob" });
      const u = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = u; a.download = format === "docx" ? "manuel-de-procedures.docx" : "manuel-de-procedures.pdf"; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiError(e, "Export du manuel indisponible")); } finally { setExportingManual(""); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-10" data-testid="atelier-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-4xl font-bold text-navy sm:text-5xl">Atelier d'écriture créative</h1>
          <p className="mt-2 text-slate-500">{section === "creative" ? "Livre, poème, essai, épopée ou pièce de théâtre — rédaction assistée par IA, export PDF et Word." : section === "reunion" ? "Compte rendu ou procès-verbal de réunion — enregistrement audio, transcription automatique, rédaction assistée par IA, exports PDF et Word." : section === "cv" ? "CV et lettres de motivation (avec ou sans proposition financière) — génération, correction, adaptation à une offre et évaluation par IA." : "Rédaction & analyse de textes littéraires et journalistiques — correction, amélioration et analyse grammaticale par IA."}</p>
        </div>
        {section === "creative" && (
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="atelier-manual-btn" variant="outline" className="rounded-full border-navy text-navy" disabled={!!exportingManual}>
                {exportingManual ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <BookText size={16} className="mr-1.5" />} Manuel de procédures <ChevronDown size={14} className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="atelier-manual-pdf" onClick={() => exportManual("pdf")} className="cursor-pointer gap-2"><Download size={14} /> Télécharger en PDF</DropdownMenuItem>
              <DropdownMenuItem data-testid="atelier-manual-docx" onClick={() => exportManual("docx")} className="cursor-pointer gap-2"><Download size={14} /> Télécharger en Word</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => { setShowLib((v) => !v); loadLibrary(); }} data-testid="atelier-library-toggle" variant="outline" className="rounded-full border-[#0E7C7B] text-[#0E7C7B]">
            <Library size={16} className="mr-1.5" /> Mes travaux{library.length ? ` (${library.length})` : ""}
          </Button>
        </div>
        )}
      </div>

      <div className="mt-6 inline-flex flex-wrap rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="atelier-section-switch">
        <button type="button" onClick={() => setSection("creative")} data-testid="atelier-section-creative"
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${section === "creative" ? "bg-navy text-white shadow" : "text-slate-500 hover:text-navy"}`}>Génération créative</button>
        <button type="button" onClick={() => setSection("litteraire")} data-testid="atelier-section-litteraire"
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${section === "litteraire" ? "bg-navy text-white shadow" : "text-slate-500 hover:text-navy"}`}>Rédaction &amp; Analyse Littéraire</button>
        {canProjets && (
          <button type="button" onClick={() => setSection("projets")} data-testid="atelier-section-projets"
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${section === "projets" ? "bg-navy text-white shadow" : "text-slate-500 hover:text-navy"}`}>Projets &amp; Appels d'offres</button>
        )}
        {user && (
          <button type="button" onClick={() => setSection("cv")} data-testid="atelier-section-cv"
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${section === "cv" ? "bg-navy text-white shadow" : "text-slate-500 hover:text-navy"}`}>Curriculum &amp; Motivation</button>
        )}
        {canProjets && (
          <button type="button" onClick={() => setSection("reunion")} data-testid="atelier-section-reunion"
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${section === "reunion" ? "bg-navy text-white shadow" : "text-slate-500 hover:text-navy"}`}>Enregistrement de réunion</button>
        )}
      </div>

      {section === "litteraire" && <LitteratureAtelier />}
      {section === "projets" && canProjets && <ProjetsAppels />}
      {section === "cv" && user && <CurriculumMotivation />}
      {section === "reunion" && canProjets && <ReunionAtelier user={user} />}

      {section === "creative" && (<>

      {showLib && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5" data-testid="atelier-library">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-navy">Mes travaux enregistrés</p>
            <button onClick={() => setShowLib(false)} data-testid="atelier-library-close" className="text-slate-400 hover:text-navy"><X size={18} /></button>
          </div>
          {library.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">Aucun travail pour l'instant — vos créations sont enregistrées automatiquement ici.</p>
          ) : (
            <>
              <div className="mt-3 flex flex-wrap gap-2">
                <input value={libQuery} onChange={(e) => setLibQuery(e.target.value)} placeholder="Rechercher par titre…" data-testid="atelier-lib-search" className="flex-1 min-w-[180px] rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy" />
                <select value={libGenre} onChange={(e) => setLibGenre(e.target.value)} data-testid="atelier-lib-genre" className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
                  <option value="all">Tous les genres</option>
                  {visibleGenres.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
                </select>
                <select value={libStatus} onChange={(e) => setLibStatus(e.target.value)} data-testid="atelier-lib-status" className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
                  <option value="all">Tous les statuts</option>
                  <option value="brouillon">Brouillons</option>
                  <option value="finalise">Finalisés</option>
                </select>
                <select value={libSort} onChange={(e) => setLibSort(e.target.value)} data-testid="atelier-lib-sort" className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
                  <option value="date">Plus récentes</option>
                  <option value="title">Titre (A→Z)</option>
                </select>
              </div>
              {(() => {
                const filtered = library.filter((w) => (libGenre === "all" || w.genre === libGenre) && (libStatus === "all" || (w.status || "brouillon") === libStatus) && (!libQuery.trim() || (w.title || "").toLowerCase().includes(libQuery.trim().toLowerCase())));
                if (libSort === "title") filtered.sort((a, b) => (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" }));
                else filtered.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
                if (filtered.length === 0) return <p className="mt-3 text-sm text-slate-400" data-testid="atelier-lib-empty">Aucune œuvre ne correspond à votre recherche.</p>;
                return (
                  <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                    {filtered.map((w) => (
                <li key={w.id} onClick={() => renameId === w.id ? null : openWork(w.id)} data-testid={`atelier-work-${w.id}`}
                  className="group cursor-pointer rounded-xl border border-slate-200 p-3 transition hover:border-[#0E7C7B] hover:bg-[#0E7C7B]/5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {renameId === w.id ? (
                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && renameWork(w.id)} autoFocus data-testid={`atelier-rename-input-${w.id}`} className="w-full rounded border border-[#0E7C7B] px-2 py-1 text-sm text-navy" />
                          <button onClick={() => renameWork(w.id)} data-testid={`atelier-rename-save-${w.id}`} className="shrink-0 text-[#0E7C7B] hover:text-[#0c6a69]"><Check size={16} /></button>
                          <button onClick={() => setRenameId(null)} className="shrink-0 text-slate-400 hover:text-navy"><X size={16} /></button>
                        </div>
                      ) : (
                        <p className="flex items-center gap-1.5 truncate font-medium text-navy"><FolderOpen size={14} className="shrink-0 text-[#0E7C7B]" /> {w.title || "Sans titre"}</p>
                      )}
                      <p className="mt-0.5 flex items-center gap-2 text-xs text-slate-400">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${(w.status || "brouillon") === "finalise" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`atelier-work-status-${w.id}`}>{(w.status || "brouillon") === "finalise" ? "Finalisé" : "Brouillon"}</span>
                        {w.genre_label} · {w.updated_at ? new Date(w.updated_at).toLocaleDateString("fr-FR") : ""}
                      </p>
                    </div>
                    {renameId !== w.id && (
                      <span className="flex shrink-0 items-center gap-2">
                        <button onClick={(e) => { e.stopPropagation(); setRenameId(w.id); setRenameVal(w.title || ""); }} data-testid={`atelier-work-rename-${w.id}`} className="text-slate-300 transition hover:text-[#0E7C7B]" title="Renommer"><Pencil size={14} /></button>
                        <button onClick={(e) => deleteWork(w.id, e)} data-testid={`atelier-work-delete-${w.id}`} className="text-slate-300 transition hover:text-red-500" title="Supprimer"><Trash2 size={15} /></button>
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 line-clamp-2 text-xs text-slate-500">{w.preview}</p>
                </li>
              ))}
                  </ul>
                );
              })()}
            </>
          )}
        </div>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-[300px_1fr]">
        <div className="space-y-3">
          {visibleGenres.map((g) => {
            const Icon = g.icon;
            return (
              <button key={g.key} onClick={() => { setGenre(g.key); setResult(""); setStatus("idle"); setWorkId(null); setInspFiles([]); setCover(null); setWorkStatus("brouillon"); setEvaluation(null); setReferences([]); setChanges(null); setPlan(null); }} data-testid={`atelier-genre-${g.key}`}
                className={`w-full rounded-2xl border p-4 text-left transition ${genre === g.key ? "border-[#0E7C7B] bg-[#0E7C7B]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                <p className="flex items-center gap-2 font-semibold text-navy"><Icon size={16} className="text-[#0E7C7B]" /> {g.label}</p>
                <p className="mt-0.5 text-xs text-slate-500">{g.desc}</p>
              </button>
            );
          })}
        </div>

        {genre === "roman" ? <RomanAtelier user={user} /> : (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="space-y-3">
            <div>
              {genre === "cours" && <div className="mb-1 flex items-center justify-between"><label className="text-xs text-slate-500">Sujet / thème du cours</label><HelpBtn k="subject" /></div>}
              <Input value={f.subject} onChange={(e) => set({ subject: e.target.value })} placeholder="Sujet / thème de l'œuvre *" data-testid="atelier-subject" />
            </div>
            <div>
              <div className="flex items-center justify-between"><label className="text-xs text-slate-500">Style d'auteur (ton / registre)</label><HelpBtn k="tone" /></div>
              <select value={f.tone} onChange={(e) => set({ tone: e.target.value })} data-testid="atelier-tone" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
                {TONES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between"><label className="text-xs text-slate-500">Longueur du texte</label><HelpBtn k="length" /></div>
              <select value={f.length} onChange={(e) => set({ length: e.target.value })} data-testid="atelier-length" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
                <option value="court">Court</option>
                <option value="moyen">Moyen</option>
                <option value="long">Long</option>
              </select>
            </div>
            <div>
              {genre === "cours" && <div className="mb-1 flex items-center justify-between"><label className="text-xs text-slate-500">Pièces jointes d'inspiration</label><HelpBtn k="attach" /></div>}
              <label
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDropFiles}
                className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-4 py-4 text-center text-xs transition ${dragOver ? "border-[#0E7C7B] bg-[#0E7C7B]/5" : "border-slate-300 hover:bg-slate-50"}`}
                data-testid="atelier-insp-attach-label">
                {attaching ? <Loader2 size={16} className="animate-spin text-[#0E7C7B]" /> : <Upload size={16} className="text-[#0E7C7B]" />}
                <span className="font-medium text-slate-600">Glissez-déposez ou cliquez pour joindre des fichiers d'inspiration</span>
                <span className="text-slate-400">Plusieurs fichiers acceptés · .pdf, .docx, .txt</span>
                <input type="file" multiple accept=".pdf,.docx,.txt" onChange={onAttachChange} className="hidden" data-testid="atelier-insp-attach" />
              </label>
              {inspFiles.length > 0 && (
                <ul className="mt-2 space-y-1.5" data-testid="atelier-insp-list">
                  {inspFiles.map((file, i) => (
                    <li key={i} data-testid={`atelier-insp-item-${i}`} className="flex items-center justify-between gap-2 rounded-lg border border-[#0E7C7B]/30 bg-[#0E7C7B]/5 p-2.5 text-xs">
                      <span className="flex min-w-0 items-center gap-1.5 font-medium text-navy"><FileText size={13} className="shrink-0 text-[#0E7C7B]" /> <span className="truncate" data-testid={`atelier-insp-name-${i}`}>{file.name}</span></span>
                      <span className="flex shrink-0 items-center gap-2">
                        <span className="text-slate-500">{Math.max(1, Math.round(file.size / 1024))} Ko</span>
                        <button type="button" onClick={() => removeInspFile(i)} data-testid={`atelier-insp-remove-${i}`} className="text-slate-400 hover:text-red-500" title="Retirer"><X size={14} /></button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {genre === "cours" && (
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <div className="mb-1 flex items-center justify-between"><label className="text-xs text-slate-500">Public / niveau</label><HelpBtn k="level" /></div>
                    <Input value={f.level} onChange={(e) => set({ level: e.target.value })} placeholder="ex. L1 médecine, lycée…" data-testid="atelier-level-input" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between"><label className="text-xs text-slate-500">Nombre de leçons / chapitres</label><HelpBtn k="lessons" /></div>
                    <input type="number" min={1} max={30} value={f.lessons} onChange={(e) => set({ lessons: e.target.value })} data-testid="atelier-lessons" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between"><label className="text-xs text-slate-500">Durée estimée (volume horaire)</label><HelpBtn k="hours" /></div>
                    <input type="text" inputMode="numeric" value={f.target_hours} onChange={(e) => set({ target_hours: e.target.value })} placeholder="ex. 20h" data-testid="atelier-target-hours" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy" />
                    <p className="mt-1 text-[11px] text-slate-400">Calibre la densité des leçons</p>
                  </div>
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between"><label className="text-xs text-slate-500">Enseignant / auteur (optionnel)</label><HelpBtn k="teacher" /></div>
                  <Input value={f.teacher} onChange={(e) => set({ teacher: e.target.value })} placeholder="Figure sur la page de garde imprimée" data-testid="atelier-teacher-input" />
                </div>
              </div>
            )}
            {genre === "livre" && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Input value={f.literary_genre} onChange={(e) => set({ literary_genre: e.target.value })} placeholder="Genre littéraire (roman, polar, SF…)" data-testid="atelier-litgenre" />
                <div>
                  <label className="text-xs text-slate-500">Nombre de chapitres</label>
                  <input type="number" min={1} max={40} value={f.chapters} onChange={(e) => set({ chapters: e.target.value })} data-testid="atelier-chapters" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy" />
                </div>
              </div>
            )}
            {genre === "poeme" && (
              <select value={f.form} onChange={(e) => set({ form: e.target.value })} data-testid="atelier-form" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
                {POEM_FORMS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </select>
            )}
            {genre === "essai" && (
              <Input value={f.thesis} onChange={(e) => set({ thesis: e.target.value })} placeholder="Thèse / angle (optionnel)" data-testid="atelier-thesis" />
            )}
            {genre === "epopee" && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Input value={f.hero} onChange={(e) => set({ hero: e.target.value })} placeholder="Héros / héroïne (optionnel)" data-testid="atelier-hero" />
                <Input value={f.setting} onChange={(e) => set({ setting: e.target.value })} placeholder="Cadre mythique (optionnel)" data-testid="atelier-setting" />
              </div>
            )}
            {genre === "theatre" && (
              <div>
                <label className="text-xs text-slate-500">Nombre d'actes</label>
                <input type="number" min={1} max={10} value={f.acts} onChange={(e) => set({ acts: e.target.value })} data-testid="atelier-acts" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy" />
              </div>
            )}
          </div>
          <Button onClick={run} disabled={status === "running"} data-testid="atelier-run" className="mt-3 rounded-full bg-navy text-white hover:bg-navy-light">
            {status === "running" ? <><Loader2 size={15} className="mr-1 animate-spin" /> Génération en cours…</> : <><Sparkles size={15} className="mr-1" /> Générer avec l'IA</>}
          </Button>
          {status === "running" && <p className="mt-2 text-xs text-slate-400">L'IA compose votre texte, cela peut prendre jusqu'à une minute…</p>}

          {result && (
            <div className="mt-4" data-testid="atelier-result">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-medium text-slate-500">Texte généré — modifiable (Markdown conservé)</p>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${workStatus === "finalise" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="atelier-work-status-badge">{workStatus === "finalise" ? "Finalisé" : "Brouillon"}</span>
              </div>
              <textarea ref={outputRef} value={result} onChange={(e) => setResult(e.target.value)} onBlur={() => saveWork(result)} data-testid="atelier-output"
                className="min-h-[360px] max-h-[560px] w-full overflow-y-auto whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700 focus:border-[#0E7C7B] focus:outline-none" />
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-slate-400" data-testid="atelier-stats">
                <span data-testid="atelier-stats-words">{stats.words} mot{stats.words > 1 ? "s" : ""}</span><span>·</span>
                <span data-testid="atelier-stats-sentences">{stats.sentences} phrase{stats.sentences > 1 ? "s" : ""}</span><span>·</span>
                <span data-testid="atelier-stats-reading">~{stats.mins} min de lecture</span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2" data-testid="atelier-ai-actions">
                <Button onClick={() => runAssist("correct")} disabled={!!busy} data-testid="atelier-ai-correct" className="rounded-full bg-navy text-xs text-white hover:bg-navy-light">
                  {busy === "correct" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Check size={13} className="mr-1" />} Corriger avec l'IA
                </Button>
                <Button onClick={() => runAssist("improve")} disabled={!!busy} data-testid="atelier-ai-improve" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">
                  {busy === "improve" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Wand2 size={13} className="mr-1" />} Améliorer / Humaniser
                </Button>
                <Button onClick={() => runAssist("plan")} disabled={!!busy} data-testid="atelier-ai-plan" variant="outline" className="rounded-full border-slate-300 text-xs text-slate-600">
                  {busy === "plan" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <ListChecks size={13} className="mr-1" />} Suggérer un plan
                </Button>
                <Button onClick={() => runAssist("evaluate")} disabled={!!busy} data-testid="atelier-ai-evaluate" variant="outline" className="rounded-full border-amber-400 text-xs text-amber-600 hover:bg-amber-50">
                  {busy === "evaluate" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <ClipboardCheck size={13} className="mr-1" />} Évaluer /20
                </Button>
                <Button onClick={() => setShowRubric((v) => !v)} data-testid="atelier-rubric-toggle" variant="outline" className="rounded-full border-slate-300 text-xs text-slate-600">
                  <SlidersHorizontal size={13} className="mr-1" /> Barème{rubricsAll[genre]?.custom ? " (personnalisé)" : ""}
                </Button>
              </div>

              {showRubric && (
                <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="atelier-rubric-panel">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-navy">Barème d'évaluation /20 — {GENRES.find((g) => g.key === genre)?.label || genre}</p>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${rubricSum === 20 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`} data-testid="atelier-rubric-sum">Total : {rubricSum} / 20</span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
                    <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600"><GraduationCap size={14} className="text-[#0E7C7B]" /> Niveau d'exigence de la notation</label>
                    <select value={evalLevel} onChange={(e) => setEvalLevel(e.target.value)} data-testid="atelier-eval-level" className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-navy">
                      {EVAL_LEVELS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
                    </select>
                    <span className="rounded-full bg-[#0E7C7B]/10 px-2 py-0.5 text-[11px] font-semibold text-[#0E7C7B]" data-testid="atelier-eval-level-badge">Notation {EVAL_LEVELS.find((l) => l.v === evalLevel)?.x}</span>
                    <span className="text-[11px] text-slate-400">— plus le niveau est élevé, plus l'évaluation est sévère</span>
                  </div>
                  <div className="mt-3 space-y-2">
                    {rubric.map((c, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`atelier-rubric-row-${i}`}>
                        <Input value={c.critere} onChange={(e) => setCrit(i, "critere", e.target.value)} placeholder="Nom du critère" data-testid={`atelier-rubric-name-${i}`} className="flex-1" />
                        <input type="number" min="1" max="20" value={c.max} onChange={(e) => setCrit(i, "max", e.target.value)} data-testid={`atelier-rubric-max-${i}`} className="w-20 rounded-lg border border-slate-200 px-2 py-2 text-sm text-navy focus:border-[#0E7C7B] focus:outline-none" />
                        <span className="text-xs text-slate-400">pts</span>
                        <button onClick={() => removeCrit(i)} data-testid={`atelier-rubric-remove-${i}`} className="text-slate-400 hover:text-red-500"><X size={16} /></button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button onClick={addCrit} data-testid="atelier-rubric-add" variant="outline" className="rounded-full border-slate-300 text-xs text-slate-600"><Plus size={13} className="mr-1" /> Ajouter un critère</Button>
                    <Button onClick={resetRubric} data-testid="atelier-rubric-reset" variant="outline" className="rounded-full border-slate-300 text-xs text-slate-600"><RotateCcw size={13} className="mr-1" /> Réinitialiser</Button>
                    <Button onClick={saveRubric} disabled={savingRubric} data-testid="atelier-rubric-save" className="rounded-full bg-[#0E7C7B] text-xs text-white hover:bg-[#0c6a69]">{savingRubric ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Save size={13} className="mr-1" />} Enregistrer le barème</Button>
                  </div>
                  <p className="mt-2 text-xs text-slate-400">Ce barème est réutilisé pour toutes vos évaluations de ce genre. Le total doit faire exactement 20 points.</p>
                </div>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button onClick={continueWriting} disabled={continuing || status === "running"} data-testid="atelier-continue" className="rounded-full bg-[#0E7C7B] text-xs text-white hover:bg-[#0c6a69]">
                  {continuing ? <><Loader2 size={13} className="mr-1 animate-spin" /> Poursuite en cours…</> : <><Sparkles size={13} className="mr-1" /> Continuer l'écriture</>}
                </Button>
                {genre === "cours" && (
                  <Button onClick={() => setShowPreview(true)} data-testid="atelier-preview" className="rounded-full bg-navy text-xs text-white hover:bg-navy-light"><BookMarked size={13} className="mr-1" /> Aperçu paginé</Button>
                )}
                <span className="mx-1 h-6 w-px bg-slate-200" />
                <Button onClick={() => { setWorkStatus("brouillon"); saveWork(result, workId, { status: "brouillon" }); toast.success("Brouillon enregistré"); }} data-testid="atelier-save-draft" variant="outline" className="rounded-full border-navy text-xs text-navy"><Save size={13} className="mr-1" /> Enregistrer brouillon</Button>
                <Button onClick={() => { setWorkStatus("finalise"); saveWork(result, workId, { status: "finalise" }); toast.success("Travail finalisé"); }} data-testid="atelier-finalize" className="rounded-full bg-emerald-600 text-xs text-white hover:bg-emerald-700"><Check size={13} className="mr-1" /> Finaliser</Button>
                <span className="mx-1 h-6 w-px bg-slate-200" />
                <span className="text-xs font-medium text-slate-400">Exporter :</span>
                <Button onClick={() => exportDoc("pdf")} disabled={!!exporting} data-testid="atelier-export-pdf" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exporting === "pdf" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} PDF</Button>
                <Button onClick={() => exportDoc("docx")} disabled={!!exporting} data-testid="atelier-export-docx" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exporting === "docx" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} Word</Button>
                <Button onClick={exportTxt} data-testid="atelier-export-txt" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]"><Download size={13} className="mr-1" /> TXT</Button>
              </div>

              {changes && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="atelier-changes">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold text-navy">{changes.kind === "correct" ? "Corrections appliquées" : "Propositions d'amélioration"}</p>
                    {changes.original !== undefined && (
                      <Button onClick={() => setShowDiff((v) => !v)} data-testid="atelier-diff-toggle" variant="outline" className="rounded-full border-slate-300 text-xs text-slate-600">
                        <GitCompare size={13} className="mr-1" /> {showDiff ? "Masquer" : "Afficher"} le surlignage
                      </Button>
                    )}
                  </div>
                  {showDiff && changes.original !== undefined && (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="atelier-diff">
                      <p className="mb-2 text-xs font-medium text-slate-500">Comparaison avant / après</p>
                      <WordDiff original={changes.original} revised={changes.revised} testid="atelier-diff-view" className="text-sm text-slate-700" />
                    </div>
                  )}
                  {changes.items.length === 0 ? <p className="mt-3 text-sm text-emerald-600">Aucune modification majeure — le texte était déjà de bonne qualité.</p> : (
                    <ul className="mt-3 space-y-2">
                      {changes.items.map((c, k) => (
                        <li key={k} className="rounded-lg bg-slate-50 p-3 text-xs" data-testid={`atelier-change-${k}`}>
                          <span className="text-red-500 line-through">{c.before}</span> <span className="mx-1 text-slate-400">→</span> <span className="font-medium text-emerald-700">{c.after}</span>
                          {(c.explanation || c.note) && <p className="mt-1 text-slate-500">{c.explanation || c.note}</p>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {plan && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="atelier-plan">
                  <div className="flex items-center justify-between"><p className="font-semibold text-navy">Plan proposé</p>
                    <Button onClick={insertPlan} data-testid="atelier-plan-insert" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">Insérer dans le texte</Button>
                  </div>
                  <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-slate-600">{plan.plan_markdown}</pre>
                  {plan.conseils?.length > 0 && <ul className="mt-2 list-disc pl-5 text-sm text-slate-500">{plan.conseils.map((c, k) => <li key={k}>{c}</li>)}</ul>}
                </div>
              )}

              {evaluation && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="atelier-evaluation">
                  <div className="flex items-center justify-between">
                    <p className="flex items-center gap-2 font-semibold text-navy"><ClipboardCheck size={17} className="text-amber-600" /> Évaluation notée <span className="rounded-full bg-[#0E7C7B]/10 px-2 py-0.5 text-[11px] font-semibold text-[#0E7C7B]" data-testid="atelier-eval-applied-level">Niveau {EVAL_LEVELS.find((l) => l.v === evalLevel)?.l}</span></p>
                    <span className="rounded-full bg-amber-500 px-3 py-1 text-sm font-bold text-white" data-testid="atelier-eval-note">{evaluation.note_globale ?? "?"} / {evaluation.sur || 20}</span>
                  </div>
                  {(evaluation.criteres || []).length > 0 && (
                    <table className="mt-3 w-full text-left text-xs">
                      <thead><tr className="border-b border-amber-200 text-slate-500">{["Critère", "Note", "Appréciation"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
                      <tbody>{evaluation.criteres.map((c, k) => (
                        <tr key={k} className="border-b border-amber-100 align-top" data-testid={`atelier-eval-crit-${k}`}>
                          <td className="px-2 py-1.5 font-medium text-navy">{c.critere}</td>
                          <td className="px-2 py-1.5 font-semibold text-amber-700">{c.note}/{c.max}</td>
                          <td className="px-2 py-1.5 text-slate-600">{c.commentaire}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  )}
                  {evaluation.appreciation_generale && <p className="mt-3 rounded-lg bg-white p-3 text-sm text-slate-600"><b className="text-navy">Appréciation générale. </b>{evaluation.appreciation_generale}</p>}
                </div>
              )}

              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="atelier-references">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="flex items-center gap-2 font-semibold text-navy"><Quote size={16} className="text-[#0E7C7B]" /> Références bibliographiques</p>
                  <div className="flex items-center gap-2">
                    <select value={citationStyle} onChange={(e) => setCitationStyle(e.target.value)} data-testid="atelier-ref-style" className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-navy">
                      {CITATION_STYLES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                    </select>
                    <Button onClick={() => runAssist("refs")} disabled={!!busy} data-testid="atelier-ref-suggest" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">
                      {busy === "refs" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Sparkles size={13} className="mr-1" />} Suggérer (IA)
                    </Button>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <Input value={newRef} onChange={(e) => setNewRef(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addManualRef()} placeholder="Ajouter une référence manuellement…" data-testid="atelier-ref-input" className="flex-1" />
                  <Button onClick={addManualRef} data-testid="atelier-ref-add" variant="outline" className="rounded-full border-slate-300 text-slate-600"><Plus size={15} /></Button>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Input value={doiInput} onChange={(e) => setDoiInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && importDoi()} placeholder="DOI (ex. 10.1038/nature12373)" data-testid="atelier-doi-input" className="flex-1 min-w-[180px]" />
                  <Button onClick={importDoi} disabled={importingRef === "doi"} data-testid="atelier-doi-import" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{importingRef === "doi" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} Importer DOI</Button>
                  <input type="file" accept=".bib,.txt" ref={bibFileRef} onChange={importBib} className="hidden" data-testid="atelier-bib-file" />
                  <Button onClick={() => bibFileRef.current?.click()} disabled={importingRef === "bib"} data-testid="atelier-bib-import" variant="outline" className="rounded-full border-slate-300 text-xs text-slate-600">{importingRef === "bib" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Upload size={13} className="mr-1" />} Importer .bib</Button>
                </div>
                {references.length === 0 ? <p className="mt-3 text-sm text-slate-400">Aucune référence. Suggérez-en via l'IA ou ajoutez-les manuellement ; elles figureront en fin de document dans les exports.</p> : (
                  <ol className="mt-3 space-y-2 text-sm">
                    {references.map((r, i) => (
                      <li key={i} data-testid={`atelier-ref-${i}`} className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
                        <span className="min-w-0 text-slate-600"><span className="mr-1 font-semibold text-navy">[{i + 1}]</span>{r.formatee || `${r.auteurs || ""} (${r.annee || ""}). ${r.titre || ""}. ${r.source || ""}`}</span>
                        <span className="flex shrink-0 items-center gap-2">
                          <button onClick={() => moveRef(i, i - 1)} disabled={i === 0} data-testid={`atelier-ref-up-${i}`} title="Monter" className="text-slate-400 hover:text-navy disabled:opacity-30"><ChevronUp size={15} /></button>
                          <button onClick={() => moveRef(i, i + 1)} disabled={i === references.length - 1} data-testid={`atelier-ref-down-${i}`} title="Descendre" className="text-slate-400 hover:text-navy disabled:opacity-30"><ChevronDown size={15} /></button>
                          <button onMouseDown={(e) => e.preventDefault()} onClick={() => insertCitation(r)} data-testid={`atelier-ref-insert-${i}`} title="Insérer l'appel de citation" className="text-slate-400 hover:text-[#0E7C7B]"><Quote size={15} /></button>
                          <button onClick={() => removeRef(i)} data-testid={`atelier-ref-remove-${i}`} title="Supprimer" className="text-slate-400 hover:text-red-500"><X size={15} /></button>
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
              {(genre === "livre" || genre === "epopee" || genre === "cours") && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="atelier-cover">
                  <div className="flex flex-wrap items-center gap-3">
                    <Button onClick={generateCover} disabled={coverLoading} data-testid="atelier-cover-generate" className="rounded-full bg-navy text-xs text-white hover:bg-navy-light">
                      {coverLoading ? <><Loader2 size={13} className="mr-1 animate-spin" /> Génération…</> : <><ImageIcon size={13} className="mr-1" /> {cover ? (genre === "cours" ? "Régénérer l'illustration" : "Régénérer la couverture") : (genre === "cours" ? "Générer une illustration de cours" : "Générer une couverture IA")}</>}
                    </Button>
                    <span className="text-xs text-slate-400">{genre === "cours" ? "Illustration pédagogique IA (nano banana) insérée en 1re page de l'export PDF/Word" : "Illustration IA (nano banana) insérée en 1re page de l'export PDF/Word"}</span>
                  </div>
                  {cover && (
                    <div className="mt-3 flex items-start gap-3" data-testid="atelier-cover-preview">
                      <img src={`data:image/png;base64,${cover}`} alt="Couverture générée" className="h-40 w-auto rounded-lg border border-slate-200 shadow-sm" data-testid="atelier-cover-img" />
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2 text-xs text-slate-600" data-testid="atelier-cover-overlay-label">
                          <input type="checkbox" checked={coverOverlay} onChange={(e) => setCoverOverlay(e.target.checked)} data-testid="atelier-cover-overlay" className="h-4 w-4 rounded border-slate-300 text-[#0E7C7B]" />
                          Incruster le titre et l'auteur sur la couverture
                        </label>
                        {coverOverlay && (
                          <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="atelier-cover-pos-wrap">
                            <span>Position :</span>
                            <select value={coverPos} onChange={(e) => setCoverPos(e.target.value)} data-testid="atelier-cover-pos" className="rounded-lg border border-slate-200 px-2 py-1 text-xs text-navy">
                              <option value="haut">Haut</option>
                              <option value="centre">Centre</option>
                              <option value="bas">Bas</option>
                            </select>
                          </div>
                        )}
                        <button type="button" onClick={() => setCover(null)} data-testid="atelier-cover-remove" className="self-start text-xs text-slate-400 hover:text-red-500">Retirer</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {parseSections(result).length > 0 && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="atelier-sections">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Régénérer une section</p>
                  <ul className="mt-2 space-y-1.5">
                    {parseSections(result).map((sec, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2" data-testid={`atelier-section-${i}`}>
                        <span className="min-w-0 truncate text-sm text-navy">{sec.title || `Section ${i + 1}`}</span>
                        <button onClick={() => regenerateSection(sec, i)} disabled={regenIdx !== null} data-testid={`atelier-regen-${i}`}
                          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#0E7C7B] px-2.5 py-1 text-xs font-medium text-[#0E7C7B] transition hover:bg-[#0E7C7B]/10 disabled:opacity-50">
                          {regenIdx === i ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Régénérer
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        )}
      </div>
      <CoursPreview
        open={showPreview}
        onClose={() => setShowPreview(false)}
        content={result}
        courseMeta={{ level: f.level, teacher: f.teacher }}
        onRegenerate={regenerateByIndex}
        regenIdx={regenIdx}
        onExport={exportDoc}
        exporting={exporting}
      />
      {helpKey && (
        <>
          <div className="fixed inset-0 z-[70] bg-black/30" data-testid="atelier-help-overlay" onClick={() => setHelpKey(null)} />
          <aside className="fixed right-0 top-0 z-[71] flex h-full w-full max-w-sm flex-col bg-white shadow-2xl" data-testid="atelier-help-panel">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <span className="flex items-center gap-2 font-semibold text-navy"><HelpCircle size={18} className="text-[#0E7C7B]" /> Aide</span>
              <button type="button" onClick={() => setHelpKey(null)} data-testid="atelier-help-close" className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              <h3 className="text-lg font-bold text-navy" data-testid="atelier-help-title">{HELP_TOPICS[helpKey]?.title}</h3>
              <div className="mt-3 space-y-3">
                {(HELP_TOPICS[helpKey]?.body || []).map((p, i) => (
                  <p key={i} className="text-sm leading-relaxed text-slate-600">{p}</p>
                ))}
              </div>
              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
                Besoin de la procédure complète ? Téléchargez le <b>Manuel de procédures</b> (bouton en haut de la page).
              </div>
            </div>
          </aside>
        </>
      )}
      </>)}
    </div>
  );
}
